import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import * as BenchlingUtil from 'BenchlingUtil';
import HomeDashboardActions from 'home-dashboard/HomeDashboardActions';
import navigateToURL from 'lib/navigateToURL';
import {JoinOrganizationListingOrganization} from 'parties/organizations/__generated/JoinOrganizationListingOrganization';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ElementMouseEvent} from 'util/Types';

export const JOIN_ORGANIZATION_LISTING_ORGANIZATION_FRAGMENT = gql`
  fragment JoinOrganizationListingOrganization on OrganizationNode {
    id
    avatarUrl
    handle
    members {
      ... on UserNode {
        id
        name
      }
    }
    name
    urlIdentifier
  }
`;

const MAX_MEMBERS_DISPLAYED = 5;

function OrganizationAvatarWithName({
  organization,
}: {
  organization: JoinOrganizationListingOrganization;
}): JSX.Element {
  const nameText = organization.name || organization.handle;

  const [isEllipsized, setIsEllipsized] = useState(false);
  const nameTextRef = React.useRef<HTMLSpanElement>(null);
  React.useEffect(() => {
    if (nameTextRef.current) {
      setIsEllipsized(nameTextRef.current.scrollWidth > nameTextRef.current.clientWidth);
    }
  }, [setIsEllipsized]);

  return (
    <TooltipWrapper tooltipTitle={isEllipsized ? nameText : ''}>
      <AvatarWithName
        avatarInfo={{...organization, avatar_url: organization.avatarUrl ?? undefined}}
        shape={AvatarShape.CIRCLE}
        size={AvatarSize.SMALL}
        showTooltip={false}
        nameTextRef={nameTextRef}
      />
    </TooltipWrapper>
  );
}

const handleClickOrganization = (
  e: ElementMouseEvent,
  organization: JoinOrganizationListingOrganization
): void => {
  if (!BenchlingUtil.isSimpleClick(e)) {
    return;
  }
  e.preventDefault();
  navigateToURL(`organizations/${organization.urlIdentifier}`);
  HomeDashboardActions.closeSearchOrgModal();
};

// This function is exported for testing.
export function getButtonText(hasJoinedOrg: boolean, hasRequestedJoinOrg: boolean): string {
  return hasJoinedOrg ? 'Already Joined' : hasRequestedJoinOrg ? 'Request Sent' : 'Request to Join';
}

// This function is exported for testing.
export function getButtonStyle(hasJoinedOrg: boolean, hasRequestedJoinOrg: boolean): ButtonStyle {
  return hasJoinedOrg
    ? ButtonStyle.TERTIARY_GRAY
    : hasRequestedJoinOrg
      ? ButtonStyle.TERTIARY_BLUE_POWDER
      : ButtonStyle.SECONDARY;
}

// This function is exported for testing.
export function getMemberText(memberNames: Array<String>): string {
  const memberNamesText = memberNames.slice(0, MAX_MEMBERS_DISPLAYED).join(', ');

  const numAdditionalMembers = memberNames.length - MAX_MEMBERS_DISPLAYED;
  return numAdditionalMembers > 0
    ? `${memberNamesText} and ${numAdditionalMembers} ${BenchlingUtil.pluralize(
        numAdditionalMembers,
        'other'
      )}`
    : memberNamesText;
}

function MemberCount({
  organization,
  showTooltip,
}: {
  organization: JoinOrganizationListingOrganization;
  showTooltip: boolean;
}): JSX.Element {
  const tooltipTitle = showTooltip ? getMemberText(organization.members.map((member) => member.name)) : '';
  return (
    <TooltipWrapper tooltipTitle={tooltipTitle} tooltipPlacement={TooltipPlacement.TOP}>
      <Col className={classNames(css(styles.count), 'u-flex-display u-small-padding-left u-faded u-italic')}>
        {/* Use members.length instead of memberCount to exclude BenchlingApp users */}
        {`${organization.members.length} members`}
      </Col>
    </TooltipWrapper>
  );
}

function OrganizationRow({
  organization,
  canNavigateToOrganizationPage,
  hasRequestedJoinOrg,
  hasJoinedOrg,
  onClickRequestJoinOrganization,
}: {
  organization: JoinOrganizationListingOrganization;
  canNavigateToOrganizationPage: boolean;
  hasRequestedJoinOrg: boolean;
  hasJoinedOrg: boolean;
  onClickRequestJoinOrganization: (organizationId: string) => void;
}): JSX.Element {
  return (
    <Row className={classNames(css(styles.organizationRow), 'u-flex-display')}>
      <Col className={css(styles.avatarName)}>
        <span
          className="u-pointer"
          onClick={(e: ElementMouseEvent) => {
            if (canNavigateToOrganizationPage) {
              handleClickOrganization(e, organization);
            }
          }}
        >
          <OrganizationAvatarWithName organization={organization} />
        </span>
      </Col>
      <MemberCount
        organization={organization}
        showTooltip={BenchlingConfig.GROWTH_ENABLE_ORGANIZATION_RECOMMENDATIONS}
      />
      <Col className={css(styles.buttonContainer)}>
        <Button
          onClick={() => {
            onClickRequestJoinOrganization(organization.id);
          }}
          className={css(styles.button)}
          disabled={hasJoinedOrg || hasRequestedJoinOrg}
          text={getButtonText(hasJoinedOrg, hasRequestedJoinOrg)}
          buttonStyle={getButtonStyle(hasJoinedOrg, hasRequestedJoinOrg)}
          size={ButtonSize.MEDIUM}
        />
      </Col>
    </Row>
  );
}

interface OrganizationListingProps {
  organizations: Array<JoinOrganizationListingOrganization>;
  alreadyJoinedOrganizationIds: Array<string>;
  requestedJoinOrganizationIds: Array<string>;
  onClickRequestJoinOrganization: (organizationId: string) => void;
  canNavigateToOrganizationPage?: boolean;
}

export default function JoinOrganizationListing(props: OrganizationListingProps): JSX.Element {
  return (
    <div className="container">
      {props.organizations.map((organization: JoinOrganizationListingOrganization) => {
        const hasRequestedJoinOrg = props.requestedJoinOrganizationIds.includes(organization.id);
        const hasJoinedOrg = props.alreadyJoinedOrganizationIds.includes(organization.id);
        return (
          <OrganizationRow
            key={organization.id}
            organization={organization}
            canNavigateToOrganizationPage={Boolean(props.canNavigateToOrganizationPage)}
            hasRequestedJoinOrg={hasRequestedJoinOrg}
            hasJoinedOrg={hasJoinedOrg}
            onClickRequestJoinOrganization={props.onClickRequestJoinOrganization}
          />
        );
      })}
    </div>
  );
}

const styles = StyleSheet.create({
  organizationRow: {
    display: 'flex',
    alignItems: 'center',
  },
  count: {
    alignItems: 'center',
    flexShrink: 0,
    marginRight: '10px',
  },
  buttonContainer: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  button: {
    ':focus': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  avatarName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
});
